import { DatabasesExternalAssetsDisplayGridRowData, TablesExternalAssetsDisplayGridRowData } from '@tableau/data-catalog-client-components';
import { parseResponse } from '@tableau/metrics-engine';

import { IDatabaseInfo } from '../../catalog/models/IDatabaseInfo';
import { ITableInfo } from '../../catalog/models/ITableInfo';
import { Databases } from '../../catalog/resources/Databases';
import { DatabasesWithCrass } from '../../catalog/resources/DatabasesWithCrass';
import { Tables } from '../../catalog/resources/Tables';
import { TablesWithCrass } from '../../catalog/resources/TablesWithCrass';
import { IDataRoleInfo } from '../../dataRole/models/IDataRoleInfo';
import { DataRoles } from '../../dataRole/resources/Dataroles';
import { IDataSourceInfo } from '../../datasource/models/IDataSourceInfo';
import { DataSources } from '../../datasource/resources/Datasources';
import { FlowListItemInfo } from '../../flow/models/FlowInfo';
import { Flows } from '../../flow/resources/Flows';
import { ILensInfo } from '../../lens/models/ILensInfo';
import { Lenses } from '../../lens/resources/Lenses';
import { getMergedMetricInfo } from '../../metrics';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { MetricDataPoints } from '../../metrics/resources/MetricDataPoints';
import { Metrics } from '../../metrics/resources/Metrics';
import { IMetricDataList } from '../../metrics/serverApi/types/IMetricDataList';
import { Projects } from '../../projects/resources/Projects';
import { IPublishedConnectionInfo } from '../../publishedConnection/models/IPublishedConnectionInfo';
import { PublishedConnections } from '../../publishedConnection/resources/PublishedConnections';
import { Recents } from '../../recents/resources/Recents';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ListResult, mapListResult } from '../../ts/fetchers/ListResult';
import { Filter } from '../../ts/helpers/Filter';
import { Order } from '../../ts/helpers/Order';
import { IProjectInfo } from '../../ts/models/IProjectInfo';
import { IViewInfo } from '../../ts/models/IViewInfo';
import { OrderKeys } from '../../ts/models/OrderKeys';
import { sortOptionsBySearchSubspace } from '../../ts/models/sort/SortOptionDefinitions';
import { IResourceQuery } from '../../ts/resources/ResourceQuery';
import { IOrderClause } from '../../ts/ServerApi/types/IOrderClause';
import { IUserWithPublicInfo } from '../../ts/ServerApi/types/IUser';
import { Users } from '../../userManagement/services/resources/Users';
import { Views } from '../../views/resources/Views';
import { IWorkbookInfo } from '../../workbook/models/IWorkbookInfo';
import { Workbooks } from '../../workbook/resources/Workbooks';
import { SearchResult } from '../models/SearchResult';
import { CatalogOmniboxItem, IMetricInfoForOmni, IPublishedConnectionInfoForOmni } from '../models/Types';

const MaxResultsPerType = 3;
const MaxResultsPerView = 6;
const MaxResultsPerMetrics = 4;

const GetUsersExtraParams = {
    'getItemCounts': true
};

export class SearchService {
    public static readonly instance = new SearchService();

    private server: SessionManagementService;
    private tables: Tables;
    private databases: Databases;
    public notifyAboutMoreDataUpdatesForCurrentSearch: (searchResult: SearchResult) => void;
    private databasesWithCrass: DatabasesWithCrass;
    private tablesWithCrass: TablesWithCrass;

    private currentRequestProgress: {
        pendingRequests: number;
        totalResults: number;
    };

    public initialize() {
        this.server = getSessionManagementServiceInstance();
    }

    public get Server(): SessionManagementService {
        return this.server;
    }

    public search(searchText: string): Promise<SearchResult> {
        let searchResult: SearchResult = {
            datasources: null,
            totalResults: 0,
            flows: null,
            projects: null,
            virtualConnections: null,
            users: null,
            views: null,
            metrics: null,
            workbooks: null,
            dataRoles: null,
            lenses: null,
            tables: null,
            databases: null,
            foundResults: false,
            tablesReturnedError: false,
            databasesReturnedError: false
        };

        const requestProgress = {
            pendingRequests: this.getPendingRequestsCount(),
            totalResults: 0
        };
        this.currentRequestProgress = requestProgress;

        return new Promise<SearchResult>((resolve, reject) => {
            const updateProgress = (result: ListResult<any>) => {
                if (this.currentRequestProgress === requestProgress) {
                    requestProgress.totalResults += result.total;
                    requestProgress.pendingRequests--;
                    if (requestProgress.pendingRequests === 0) {

                        this.currentRequestProgress = null;
                        searchResult.foundResults = requestProgress.totalResults > 0;
                        searchResult.totalResults = requestProgress.totalResults;

                        resolve(searchResult);
                    }
                }
            };

            this.updateType<IProjectInfo>(
                searchText,
                Projects.all(),
                sortOptionsBySearchSubspace['projects'].defaultOrder,
                (result: ListResult<IProjectInfo>) => {
                    searchResult.projects = result;
                    updateProgress(result);
                }
            );
            if (FeatureFlags.publishedConnectionContentManagementEnabled) {
                this.updateType<IPublishedConnectionInfoForOmni>(
                    searchText,
                    PublishedConnections.all(),
                    sortOptionsBySearchSubspace['virtualConnections'].defaultOrder,
                    (result: ListResult<IPublishedConnectionInfo>) => {
                        searchResult.virtualConnections = result;
                        updateProgress(result);
                    }
                );
            }
            if (!this.server.isLimitedUserVisibilityEnforced()) {
                this.updateType<IUserWithPublicInfo>(
                    searchText,
                    Users.instance.all().withExtraParams(GetUsersExtraParams),
                    [Order.asc(OrderKeys.displayName)],
                    (result: ListResult<IUserWithPublicInfo>) => {
                        searchResult.users = result;
                        updateProgress(result);
                    }
                );
            }
            this.updateType<IViewInfo>(
                searchText,
                Views.all(),
                sortOptionsBySearchSubspace['views'].defaultOrder,
                (result: ListResult<IViewInfo>) => {
                    searchResult.views = result;
                    updateProgress(result);
                },
                MaxResultsPerView
            );
            if (FeatureFlags.metricsSpaceEnabled) {
                this.updateType<IMetricInfoForOmni>(
                    searchText,
                    Metrics.all(),
                    sortOptionsBySearchSubspace['metrics'].defaultOrder,
                    (result: ListResult<IMetricInfo>) => {
                        searchResult.metrics = result;
                        updateProgress(result);

                        if (result.total > 0) {
                            this.getMetricValues(searchResult);
                        }
                    }
                    , MaxResultsPerMetrics);
            }
            this.updateType<IWorkbookInfo>(
                searchText,
                Workbooks.all(),
                sortOptionsBySearchSubspace['workbooks'].defaultOrder,
                (result: ListResult<IWorkbookInfo>) => {
                    searchResult.workbooks = result;
                    updateProgress(result);
                }
            );
            this.updateType<IDataSourceInfo>(
                searchText,
                DataSources.published(),
                sortOptionsBySearchSubspace['datasources'].defaultOrder,
                (result: ListResult<IDataSourceInfo>) => {
                    searchResult.datasources = result;
                    updateProgress(result);
                }
            );
            if (this.server.flowsOnSiteEnabled()) {
                this.updateType<FlowListItemInfo>(
                    searchText,
                    Flows.all(),
                    sortOptionsBySearchSubspace['flows'].defaultOrder,
                    (result: ListResult<FlowListItemInfo>) => {
                        searchResult.flows = result;
                        updateProgress(result);
                    }
                );
            }
            if (this.server.catalogingOnSiteEnabled() && this.server.catalogingOnServerEnabled()) {
                const tablesResourceQuery = TablesWithCrass.all();

                tablesResourceQuery
                    .addFilter(Filter.search(searchText))
                    .withOrder([Order.desc(OrderKeys.relevance)])
                    .withExtraParams({ isOmnibox: true })
                    .getSlice(0, MaxResultsPerType)
                    .then((result: ListResult<ITableInfo>) => {
                        searchResult.tablesReturnedError = false;
                        searchResult.tables = mapListResult(result, this.mapCatalogItem);
                        updateProgress(result);

                    }).catch((reason: any) => {
                        // Data Catalog might down, indexing, ingesting, etc. so just show 0 results and an error message.
                        const emptyList = ListResult.empty<CatalogOmniboxItem>();
                        searchResult.tables = emptyList;
                        searchResult.tablesReturnedError = true;

                        updateProgress(emptyList);
                    });

                const databasesResourceQuery = DatabasesWithCrass.all();

                databasesResourceQuery
                    .addFilter(Filter.search(searchText))
                    .withOrder([Order.desc(OrderKeys.relevance)])
                    .withExtraParams({ isOmnibox: true })
                    .getSlice(0, MaxResultsPerType)
                    .then((result: ListResult<IDatabaseInfo>) => {
                        searchResult.databasesReturnedError = false;
                        searchResult.databases = mapListResult(result, this.mapCatalogItem);
                        updateProgress(result);

                    }).catch((reason: any) => {
                        // Data Catalog might down, indexing, ingesting, etc. so just show 0 results and an error message.
                        const emptyList = ListResult.empty<CatalogOmniboxItem>();
                        searchResult.databases = emptyList;
                        searchResult.databasesReturnedError = true;

                        updateProgress(emptyList);
                    });
            }

            this.updateType<IDataRoleInfo>(
                searchText,
                DataRoles.all(),
                sortOptionsBySearchSubspace['dataRoles'].defaultOrder,
                (result: ListResult<IDataRoleInfo>) => {
                    searchResult.dataRoles = result;
                    updateProgress(result);
                }
            );

            this.updateType<ILensInfo>(
                searchText,
                Lenses.all(),
                sortOptionsBySearchSubspace['lenses'].defaultOrder,
                (result: ListResult<ILensInfo>) => {
                    searchResult.lenses = result;
                    updateProgress(result);
                }
            );
        });
    }

    private updateType<T>(searchText: string, query: IResourceQuery<T>, order: IOrderClause[], updateFn: (result: ListResult<T>) => any, maxResultPerType = MaxResultsPerType) {
        query
            .addFilter(Filter.search(searchText))
            .withOrder(order)
            .getSlice(0, maxResultPerType)
            .then((result: ListResult<T>) => {
                updateFn(result);
            });
    }

    private getPendingRequestsCount() {
        let total = 6;
        if (this.server.catalogingOnSiteEnabled()) {
            total = total + 2;  // 1 for tables, 1 more for databases
        }

        if (this.server.flowsOnSiteEnabled()) {
            total = total + 1;
        }

        if (!this.server.isLimitedUserVisibilityEnforced()) {
            total = total + 1;  // users call to happen only if limited user visbility is not enforced
        }

        if (FeatureFlags.metricsSpaceEnabled) {
            total = total + 1;
        }

        if (FeatureFlags.publishedConnectionContentManagementEnabled) {
            total = total + 1;
        }

        return total;
    }

    private getMetricValues(searchResult: SearchResult) {
        const ids: string[] = searchResult.metrics.items.map((item: IMetricInfo) => item.id);

        MetricDataPoints.getMetricDataPoints(ids)
            .then((metricDataList: IMetricDataList) => {

                for (let index in searchResult.metrics.items) {
                    let metric: IMetricInfoForOmni = searchResult.metrics.items[index];
                    const data = metricDataList.data[metric.id];

                    if (data) {
                        const result = parseResponse(data);
                        metric = getMergedMetricInfo(metric, metricDataList);
                        if (result.isOk()) {
                            metric.response = result.unwrap();
                        }
                        // update metric in array
                        this.copyProperties(metric, searchResult.metrics.items[index]);
                    } // otherwise, no need to update

                }

                if (this.notifyAboutMoreDataUpdatesForCurrentSearch) {
                    this.notifyAboutMoreDataUpdatesForCurrentSearch(searchResult);
                }
            });
    }

    private mapCatalogItem(item: TablesExternalAssetsDisplayGridRowData | DatabasesExternalAssetsDisplayGridRowData | ITableInfo | IDatabaseInfo): CatalogOmniboxItem {
        return {
            id: item.id,
            name: item.name,
            isCertified: item.isCertified,
            workbookCount: item.downstreamWorkbooksConnection ? item.downstreamWorkbooksConnection.totalCount : 0,
            datasourceCount: item.downstreamDatasourcesConnection ? item.downstreamDatasourcesConnection.totalCount : 0,
        };
    }

    private copyProperties(source: any, target: any) {
        Object.keys(source).map(property => target[property] = source[property]);
    }

    public recentUsers() {
        return Recents.users().getSlice(0, 100);
    }

    public usersWithProfilePictures(ids: string[]) {
        return Users.instance.getUsersById(ids);
    }
}
